import { yupResolver } from '@hookform/resolvers/yup';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  HStack,
  SimpleGrid,
  Select,
  InputGroup,
  InputRightAddon,
  Input,
  Textarea,
} from '@wegroup/design-system';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { CompanyLease, Lease } from '../../../../types/CompanyScanTypes';

interface Props {
  companyLease?: CompanyLease;
  isOpen: boolean;
  onClose: () => void;
  addCompanyLease: (companyLease: CompanyLease) => void;
  editCompanyLease: (companyLease: CompanyLease) => void;
}

const companyLeaseValidationSchema = yup.object().shape({
  type: yup.string().required('Required'),
  duration: yup.number().typeError('Required'),
  amount: yup.number().typeError('Required'),
  purpose: yup.string(),
  leasing_company: yup.string(),
});

const NewCompanyLeaseModal: React.FC<React.PropsWithChildren<Props>> = ({
  companyLease,
  addCompanyLease,
  editCompanyLease,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();

  const modalGoal = companyLease ? 'EDIT' : 'ADD';

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<CompanyLease>({
    resolver: yupResolver(companyLeaseValidationSchema),
    defaultValues: {
      type: companyLease?.type,
      duration: companyLease?.duration,
      amount: companyLease?.amount,
      purpose: companyLease?.purpose,
      leasing_company: companyLease?.leasing_company,
    },
  });

  const handleOnClose = () => {
    reset();
    onClose();
  };

  const handleOnSubmit = handleSubmit((newCompanyLease: CompanyLease) => {
    if (companyLease) editCompanyLease(newCompanyLease);
    else addCompanyLease(newCompanyLease);
    handleOnClose();
  });

  return (
    <Modal isOpen={isOpen} onClose={handleOnClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t(`FLOWS_COMPANY-SCAN_COMPANY-LEASE-INPUT_${modalGoal}-LEASE`)}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody as={SimpleGrid} columns={2} gap="4">
          <FormControl isInvalid={!!errors.type} gridColumn="span 2">
            <FormLabel>{t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_TYPE')}</FormLabel>
            <Select {...register('type')}>
              {Object.keys(Lease).map((key) => (
                <option key={key} value={key}>
                  {t(`FLOWS_COMPANY-SCAN_LEASE-ENUM_${key}`)}
                </option>
              ))}
            </Select>
            {errors.type?.message && (
              <FormErrorMessage>{t(errors.type.message)}</FormErrorMessage>
            )}
          </FormControl>

          <FormControl isInvalid={!!errors.duration}>
            <FormLabel>
              {t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_DURATION')}
            </FormLabel>
            <Input
              {...register('duration')}
              placeholder={t('MONTHS')}
              type="number"
            />
            {errors.duration?.message && (
              <FormErrorMessage>{t(errors.duration.message)}</FormErrorMessage>
            )}
          </FormControl>

          <FormControl isInvalid={!!errors.amount}>
            <FormLabel>
              {t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_AMOUNT')}
            </FormLabel>
            <InputGroup>
              <Input {...register('amount')} placeholder="0.00" type="number" />
              <InputRightAddon>€</InputRightAddon>
            </InputGroup>
            {errors.amount?.message && (
              <FormErrorMessage>{t(errors.amount.message)}</FormErrorMessage>
            )}
          </FormControl>

          <FormControl isInvalid={!!errors.purpose} gridColumn="span 2">
            <FormLabel>
              {t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_PURPOSE')}
            </FormLabel>

            <Textarea {...register('purpose')} />
            {errors.purpose?.message && (
              <FormErrorMessage>{t(errors.purpose.message)}</FormErrorMessage>
            )}
          </FormControl>

          <FormControl isInvalid={!!errors.leasing_company} gridColumn="span 2">
            <FormLabel>
              {t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_LEASING-COMPANY')}
            </FormLabel>
            <Input {...register('leasing_company')} />
            {errors.leasing_company?.message && (
              <FormErrorMessage>
                {t(errors.leasing_company.message)}
              </FormErrorMessage>
            )}
          </FormControl>
        </ModalBody>

        <ModalFooter as={HStack} justify="flex-end" spacing="4">
          <Button variant="ghost" mr={3} onClick={handleOnClose}>
            {t('CLOSE')}
          </Button>
          <Button onClick={handleOnSubmit}>
            {modalGoal ? t('SAVE') : t('ADD')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NewCompanyLeaseModal;
